






































import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { isIOS, isSafari } from "@/lib/browserUtils";
import { getElement } from "@/lib/elementHelpers";
import BaseButton from "@/components/BaseButton";

@Component({ name: "InfiniteScroll", components: { BaseButton } })
export default class InfiniteScroll extends Vue {
  @Prop({ type: Array })
  public readonly items!: any[] | undefined;

  @Prop({ type: Number, default: 0 })
  public readonly startPosition!: number;

  @Prop({ type: Number, default: 20 })
  public readonly pageSize!: number;

  @Prop({ type: Boolean, required: true })
  public readonly enable!: boolean;

  public loadingNext = false;

  public loadingPrev = false;

  public currentPosition = 0;

  public endPosition = 0;

  public isScrolling = false;

  public observer!: IntersectionObserver;

  public get itemsToPrint() {
    return this.items?.slice(this.currentPosition, this.endPosition);
  }

  public get allPrinted() {
    if (!this.items) return true;
    return this.items.length === this.itemsToPrint?.length;
  }

  public get showPrevButton() {
    return (this.enable && !!this.$listeners.prev) || this.currentPosition > 0 || this.loadingPrev;
  }

  public get showNextButton() {
    return (this.enable && !!this.$listeners.next) || this.endPosition < (this.items?.length ?? 0) || this.loadingNext;
  }

  public async loadNext() {
    this.loadingNext = true;
    if (this.endPosition === (this.items?.length ?? 0)) {
      this.$emit("next", () => {
        this.loadingNext = false;
        this.endPosition = Math.min(this.endPosition + this.pageSize, this.items?.length ?? 0);
      });
      return;
    }
    await new Promise(r => setTimeout(r, 50));
    this.endPosition = Math.min(this.endPosition + this.pageSize, this.items?.length ?? 0);
    this.loadingNext = false;
  }

  public async loadPrev() {
    this.loadingPrev = true;
    if (this.allPrinted) {
      this.$emit("prev", () => (this.loadingPrev = false));
      return;
    }

    const initial = document.documentElement.scrollHeight;
    if (isSafari() || isIOS()) {
      await new Promise<void>(r => {
        let i = setInterval(() => {
          if (!this.isScrolling) {
            clearInterval(i);
            r();
          }
        }, 10);
      });
    }
    await new Promise(r => setTimeout(r, 50));
    this.currentPosition = Math.max(this.currentPosition - this.pageSize, 0);
    this.loadingPrev = false;
    await this.$nextTick();
    if (Math.abs(document.documentElement.scrollHeight - initial) > 0) {
      window.scrollBy({
        top: document.documentElement.scrollHeight - initial,
        behavior: "auto",
      });
    }
  }

  public onObserve(entries: IntersectionObserverEntry[]) {
    if (this.allPrinted && !this.enable) return;
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const fromTop = entry.target === this.$refs.observerPrev;
      if (fromTop) {
        this.loadPrev();
      } else this.loadNext();
    });
  }

  public countInitialPosition() {
    const length = this.items?.length ?? 0;
    this.currentPosition = Math.max(Math.min(this.startPosition, length - this.pageSize), 0);
    this.endPosition = Math.min(this.currentPosition + this.pageSize, length);
  }

  public mounted() {
    this.countInitialPosition();
    this.$refs.observerPrev && this.observer.observe(getElement(this.$refs.observerPrev));
    this.$refs.observerNext && this.observer.observe(getElement(this.$refs.observerNext));
  }

  public created() {
    this.observer = new IntersectionObserver(this.onObserve, {
      rootMargin: "20% 0px 20% 0px",
      threshold: 1,
    });
    let timeout = 0;
    const onScroll = () => {
      clearTimeout(timeout);
      this.isScrolling = true;
      timeout = (setTimeout(() => {
        this.isScrolling = false;
      }, 150) as any) as number;
    };
    window.addEventListener("scroll", onScroll);
    this.$once("hook:beforeDestroy", () => {
      this.observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    });
  }
}
